import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight, FileText, GraduationCap, List, MoreHorizontal, Plus } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import dayjs from '../lib/dates';
import { InstructorLesson } from '../types';
import { isOfferedOpenSlot } from '../lib/openSlot';
import { Badge, Button, Menu, Segmented, cx, useToast } from '../components/charte';
import { Sheet } from '../components/shell/Sheet';
import { useUiStore } from '../store/ui';
import WeekCalendar from './planning/WeekCalendar';
import { LessonCard, OpenSlotCard } from '../components/lesson/LessonCard';
import { OpenSlotSheet, useOpenSlotsAllowed } from '../components/lesson/OpenSlotActions';

// Mon planning (refonte du 14/10/2026, tablette d'abord) : la semaine en liste par jour, comme avant ; bandeau
// des 7 jours pour sauter à un jour (collé sous l'en-tête à partir de la tablette), retour à aujourd'hui,
// feuille d'émargement dans « ··· ». Vue Calendrier au choix (grille de la semaine), mémorisée sur l'appareil.

type View = 'liste' | 'calendrier';
const VIEW_KEY = 'pmd-moniteur-planning-vue';
const readView = (): View => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'calendrier' ? 'calendrier' : 'liste';
  } catch {
    return 'liste';
  }
};

type Accompaniment = Awaited<ReturnType<typeof instructorApi.getAccompaniments>>[number];

const dayKey = (iso: string) => iso.slice(0, 10);
const zonedDay = (a: Accompaniment) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: a.timezone || 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(a.starts_at),
  );
const zonedTime = (a: Accompaniment, iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { timeZone: a.timezone || 'Europe/Paris', hour: '2-digit', minute: '2-digit' });

/** Leçon du planning : même carte que Ma journée, avec l'horaire. */
function LessonItem({ lesson }: { lesson: InstructorLesson }) {
  return lesson.isOpenSlot ? <OpenSlotCard lesson={lesson} showTime /> : <LessonCard lesson={lesson} showTime />;
}

function AccompanimentCard({ a }: { a: Accompaniment }) {
  return (
    <div className="flex items-center gap-3 rounded-card bg-surface-indigo p-4 ring-1 ring-primary-100">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-primary">
        <GraduationCap size={18} aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="font-semibold text-primary-900">Accompagnement examen · {a.candidate_name}</p>
        <p className="text-sm text-primary-700">
          {zonedTime(a, a.starts_at)} – {zonedTime(a, a.ends_at)} · {a.center}
        </p>
      </div>
    </div>
  );
}

export default function PlanningView() {
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [view, setViewState] = useState<View>(readView);
  const setView = (next: View) => {
    setViewState(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Stockage indisponible : le choix vaut pour cette visite.
    }
  };
  // Leçon ouverte depuis le calendrier ; refermée quand le livret et résumé de la séance s'ouvre par-dessus.
  const [opened, setOpened] = useState<InstructorLesson | null>(null);
  const bilanTarget = useUiStore((s) => s.bilanTarget);
  useEffect(() => {
    if (bilanTarget) setOpened(null);
  }, [bilanTarget]);
  const weekRef = dayjs().add(offset, 'week').toISOString();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['planning', offset],
    queryFn: () => instructorApi.getPlanning(weekRef),
  });

  const accompaniment = useQuery({
    queryKey: ['exam-accompaniments', data?.weekStart, data?.weekEnd],
    queryFn: () => instructorApi.getAccompaniments(dayKey(data!.days[0].date), dayKey(data!.days[data!.days.length - 1].date)),
    enabled: !!data?.days.length,
    refetchInterval: 30000,
  });
  const allLessons = data?.days.flatMap((d) => d.lessons) ?? [];
  const lessonCount = allLessons.filter((l) => !l.isOpenSlot).length;
  const openSlotCount = allLessons.filter((l) => isOfferedOpenSlot(l)).length;
  const accompanimentsOf = (date: string) => accompaniment.data?.filter((a) => zonedDay(a) === dayKey(date)) ?? [];

  const [downloadingSheet, setDownloadingSheet] = useState(false);
  // « Ouvrir un créneau » : seulement si l'auto-école le permet (Réglages › App moniteur, côté bureau).
  const canOpenSlots = useOpenSlotsAllowed();
  const [openingSlot, setOpeningSlot] = useState(false);
  const handleDownloadSheet = async () => {
    if (!data) return;
    setDownloadingSheet(true);
    try {
      const from = dayjs(data.weekStart).format('YYYY-MM-DD');
      const to = dayjs(data.weekEnd).format('YYYY-MM-DD');
      const blob = await instructorApi.downloadAttendanceSheet(from, to);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emargement-${from}_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast('Téléchargement de la feuille impossible.');
    } finally {
      setDownloadingSheet(false);
    }
  };

  const goToDay = (date: string) =>
    document.getElementById(`jour-${dayKey(date)}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-title-md text-heading sm:text-title-lg">Mon planning</h1>
          <p className="mt-1 text-sm text-secondary">Vos leçons et accompagnements aux examens.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canOpenSlots && (
            <Button icon={Plus} onClick={() => setOpeningSlot(true)}>
              Ouvrir un créneau
            </Button>
          )}
          <Segmented
            label="Affichage du planning"
            value={view}
            onChange={setView}
            options={[
              { id: 'liste', label: 'Liste', icon: List },
              { id: 'calendrier', label: 'Calendrier', icon: CalendarDays },
            ]}
          />
          <Menu
            entries={[
              {
                label: downloadingSheet ? 'Préparation de la feuille…' : "Feuille d'émargement (PDF)",
                hint: 'semaine affichée',
                icon: FileText,
                disabled: downloadingSheet || !data,
                onSelect: () => void handleDownloadSheet(),
              },
            ]}
            trigger={(props) => <Button {...props} iconOnly icon={MoreHorizontal} aria-label="Plus d'options du planning" />}
          />
        </div>
      </header>

      {/* Semaine + bandeau des jours : collés sous l'en-tête à partir de la tablette. */}
      <div
        className={cx(
          'z-20 -mx-4 space-y-3 bg-surface-light/95 px-4 pb-2 pt-1 backdrop-blur sm:-mx-6 sm:px-6',
          view === 'liste' && 'md:sticky md:top-16',
        )}
      >
        <div className="flex items-center gap-2 rounded-card bg-white p-2 shadow-card">
          <Button variant="ghost" iconOnly icon={ChevronLeft} aria-label="Semaine précédente" onClick={() => setOffset((o) => o - 1)} className="h-11 w-11" />
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-[15px] font-semibold text-heading">
              {data ? `Semaine du ${dayjs(data.weekStart).format('D')} au ${dayjs(data.weekEnd).format('D MMMM YYYY')}` : '…'}
            </p>
            <p className="text-xs text-secondary">
              {lessonCount} leçon(s)
              {openSlotCount > 0 && ` · ${openSlotCount} créneau(x) ouvert(s)`}
            </p>
          </div>
          {offset !== 0 && (
            <Button variant="soft" size="sm" onClick={() => setOffset(0)} className="max-sm:hidden">
              Cette semaine
            </Button>
          )}
          <Button variant="ghost" iconOnly icon={ChevronRight} aria-label="Semaine suivante" onClick={() => setOffset((o) => o + 1)} className="h-11 w-11" />
        </div>

        {data && view === 'liste' && (
          <nav aria-label="Jours de la semaine" className="grid grid-cols-7 gap-1.5">
            {data.days.map((day) => {
              const today = dayjs(day.date).isSame(dayjs(), 'day');
              const count =
                day.lessons.filter((l) => !l.isOpenSlot && l.status !== 'cancelled').length + accompanimentsOf(day.date).length;
              return (
                <button
                  key={day.date}
                  type="button"
                  onClick={() => goToDay(day.date)}
                  aria-label={`${dayjs(day.date).format('dddd D MMMM')}, ${count} rendez-vous`}
                  className={cx(
                    'flex min-h-[60px] flex-col items-center justify-center rounded-row px-1 py-1.5 transition-colors',
                    today ? 'bg-primary text-white shadow-btn' : 'bg-white text-body shadow-card hover:bg-primary-50',
                  )}
                >
                  <span className={cx('text-[11px] font-medium capitalize', today ? 'text-white/80' : 'text-secondary')}>
                    {dayjs(day.date).format('ddd')}
                  </span>
                  <span className="text-lg font-semibold leading-tight tabular-nums">{dayjs(day.date).format('D')}</span>
                  <span
                    className={cx('h-1.5 w-1.5 rounded-full', count ? (today ? 'bg-white' : 'bg-primary') : 'bg-transparent')}
                    aria-hidden
                  />
                </button>
              );
            })}
          </nav>
        )}
        {offset !== 0 && (
          <Button variant="soft" size="sm" onClick={() => setOffset(0)} className="w-full sm:hidden">
            Revenir à cette semaine
          </Button>
        )}
      </div>

      {isLoading && <p className="text-secondary">Chargement du planning…</p>}
      {isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger le planning : {(error as Error).message}
        </p>
      )}
      {accompaniment.isError && (
        <p role="alert" className="flex flex-wrap items-center gap-2 rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger les accompagnements aux examens.
          <Button variant="link" size="sm" onClick={() => void accompaniment.refetch()}>
            Réessayer
          </Button>
        </p>
      )}

      {data && view === 'calendrier' && (
        <WeekCalendar
          days={data.days}
          accompanimentsOf={(date) =>
            accompanimentsOf(date).map((a) => ({ id: a.id, candidate_name: a.candidate_name, center: a.center, start: a.starts_at, end: a.ends_at }))
          }
          onOpen={setOpened}
        />
      )}
      {opened && (
        <Sheet title={`Leçon du ${dayjs(opened.startsAt).format('dddd D MMMM')}`} onClose={() => setOpened(null)} className="sm:max-w-xl">
          <LessonItem lesson={opened} />
        </Sheet>
      )}

      {data && view === 'liste' && (
        <div className="space-y-6">
          {data.days.map((day) => {
            const today = dayjs(day.date).isSame(dayjs(), 'day');
            const accompaniments = accompanimentsOf(day.date);
            const empty = day.lessons.length === 0 && accompaniments.length === 0;
            return (
              <section key={day.date} id={`jour-${dayKey(day.date)}`} className="scroll-mt-24 md:scroll-mt-[232px]">
                <div className="mb-3 flex items-center gap-2">
                  <h2 className="text-[15px] font-semibold capitalize text-heading">{dayjs(day.date).format('dddd D MMMM')}</h2>
                  {today && <Badge tone="todo">Aujourd'hui</Badge>}
                </div>
                {empty ? (
                  <p className="rounded-row border border-dashed border-line px-4 py-3 text-sm text-muted">Aucune leçon planifiée</p>
                ) : (
                  <div className="space-y-3">
                    {accompaniments.map((a) => (
                      <AccompanimentCard key={a.id} a={a} />
                    ))}
                    {day.lessons.map((l) => (
                      <LessonItem key={l.id} lesson={l} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
      {openingSlot && <OpenSlotSheet onClose={() => setOpeningSlot(false)} />}
    </section>
  );
}
